import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Button, ButtonVariant } from '../../common/Button'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../../common/Icon/Icon'
import { reverseUrl } from '../../../navigation/reverseUrl'
import useShowGettingStarted from '../../../views/society/GettingStartedView/useShowGettingStarted'
import {
  CONSTANTS_SOCIETY_GUIDES,
  setGuideStep,
  societyGuidesQueries,
  TSocietyGuideStepGetToKnow,
} from '../../../api/society-guides'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../helpers/society'

const GetToKnow = ({
  title,
  description,
  getToKnowId,
  className,
}: {
  title: string
  description: string
  getToKnowId: TSocietyGuideStepGetToKnow
  className?: string
}): React.ReactNode => {
  const { showGetToKnow, society } = useShowGettingStarted()
  const { translate } = useAppTranslation()
  const queryClient = useQueryClient()

  const authUserId = useAuthenticatedUserId()
  const currentUserIsAdmin = isUserAdminInSociety(society, authUserId as string)
  const { data: guide } = useQuery(
    societyGuidesQueries.getToKnow(society?._id, currentUserIsAdmin)
  )
  const navigate = useNavigate()
  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      // eslint-disable-next-line @typescript-eslint/no-shadow
      societyId,
      id,
      completed,
    }: {
      societyId: string
      id: string
      completed: boolean
    }) => {
      return setGuideStep({
        societyId,
        id,
        completed,
        guideId: CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW,
      })
    },
    onSuccess: async (response, variables) => {
      const { data } = response.data
      if (data) {
        queryClient.setQueryData(
          societyGuidesQueries.getToKnow(variables.societyId).queryKey,
          data
        )
      }
      navigate(reverseUrl('management:get-to-know', { id: society?._id }))
    },
  })

  if (!showGetToKnow || !society || !guide) {
    return null
  }

  const getToKnowItem = guide?.steps?.find((item) => item.id === getToKnowId)
  const index = guide?.steps?.findIndex((item) => item.id === getToKnowId) || 0
  const completed = !!getToKnowItem?.completed
  return (
    <div
      className={`rounded-md bg-brandLightBrown border border-brandBrown p-5 w-fit ${className}`}
    >
      <div className="flex gap-2 mb-2">
        {completed ? (
          <Icon icon={IconChoices.CHECK_CIRCLE} highlighted />
        ) : (
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: theme.colors.white,
              borderColor: theme.colors.almostBrandBrown,
              borderWidth: 1,
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            <div style={theme.textVariants.base}>{index + 1}</div>
          </div>
        )}
        <p style={theme.textVariants.baseBold}>{title}</p>
      </div>
      <p style={{ ...theme.textVariants.base, marginBottom: theme.spacing.m }}>
        {description}
      </p>
      <div className="flex gap-4">
        <Button
          label={translate('common.actions.back')}
          onClick={() =>
            navigate(reverseUrl('management:get-to-know', { id: society._id }))
          }
          icon={IconChoices.ARROW_LEFT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          label={
            completed
              ? translate('getToKnowScreen.screens.button.undo')
              : translate('getToKnowScreen.screens.button.complete')
          }
          loading={isPending}
          onClick={() => {
            mutate({
              completed: !completed,
              id: getToKnowId,
              societyId: society._id,
            })
          }}
        />
      </div>
    </div>
  )
}

export default GetToKnow
